import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  FormControlLabel,
  Switch,
  Grid,
  IconButton,
  Box,
  Typography,
  InputAdornment,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import QrCodeIcon from '@mui/icons-material/QrCode';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { addProduct } from '../services/productService';
import { getCategories, addCategory } from '../../companysettings/services/productCategoryService';
import { getSuppliers, addSupplier } from '../../suppliers/services/supplierService';
import { getProductBrands, addProductBrand } from '../../productbrand/services/productBrandService';

const AddProductDialog = ({ 
  open, 
  onClose, 
  onProductCreated,
  suppliersFromStore = [],
  brandsFromStore = [],
  categoriesFromStore = [],
  onSupplierCreated,
  onBrandCreated,
  onCategoryCreated,
  initialName = '', // Initial product name from search
}) => {
  const { t } = useTranslation();
  
  const generateBarcode = () => {
    const timestamp = Date.now().toString().slice(-8);
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    return `${timestamp}${random}`;
  };
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    sku: '',
    barcode: '',
    categoryId: '',
    price: '',
    costPrice: '',
    stockQuantity: '',
    minStockLevel: '',
    supplierId: '',
    productBrandId: '',
    modelNumber: '',
    isActive: true,
  });
  
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState(''); // Server-level error message
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [brands, setBrands] = useState([]);
  
  // Nested dialog states
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [openSupplierDialog, setOpenSupplierDialog] = useState(false);
  const [openBrandDialog, setOpenBrandDialog] = useState(false);
  
  const [newCategory, setNewCategory] = useState({ name: '' });
  const [newSupplier, setNewSupplier] = useState({ 
    name: '', 
    contactPerson: '', 
    email: '', 
    phone: '', 
    address: '' 
  });
  const [newBrand, setNewBrand] = useState({ 
    BrandName: '', 
    Description: '', 
    LogoUrl: '', 
    Website: '' 
  });

  useEffect(() => {
    if (open) {
      // Set initial name if provided
      if (initialName) {
        setFormData(prev => ({ ...prev, name: initialName }));
      }
      
      // Use data from store if provided, otherwise fetch from API
      if (categoriesFromStore.length > 0) {
        setCategories(categoriesFromStore);
      }
      if (suppliersFromStore.length > 0) {
        setSuppliers(suppliersFromStore);
      }
      if (brandsFromStore.length > 0) {
        setBrands(brandsFromStore);
      }
      
      // Fetch data only if not provided from store
      if (categoriesFromStore.length === 0 || suppliersFromStore.length === 0 || brandsFromStore.length === 0) {
        fetchInitialData();
      }
    }
  }, [open, initialName]);

  const fetchInitialData = async () => {
    try {
      const promises = [];
      
      if (categoriesFromStore.length === 0) {
        promises.push(getCategories());
      }
      if (suppliersFromStore.length === 0) {
        promises.push(getSuppliers());
      }
      if (brandsFromStore.length === 0) {
        promises.push(getProductBrands());
      }
      
      const results = await Promise.all(promises);
      let resultIndex = 0;
      
      if (categoriesFromStore.length === 0) {
        setCategories(results[resultIndex]?.payload || results[resultIndex] || []);
        resultIndex++;
      }
      if (suppliersFromStore.length === 0) {
        setSuppliers(results[resultIndex]?.payload || results[resultIndex] || []);
        resultIndex++;
      }
      if (brandsFromStore.length === 0) {
        setBrands(results[resultIndex]?.payload || results[resultIndex] || []);
      }
    } catch (error) {
      console.error('Error fetching initial data:', error);
      toast.error(t('failed_to_load_data') || 'Failed to load data');
    }
  };

  const handleInputChange = (event) => {
    const { name, value, type, checked } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    
    // Clear server error when user makes changes
    if (serverError) {
      setServerError('');
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t('product_name_required') || 'Product name is required';
    }
    
    if (!formData.categoryId) {
      newErrors.categoryId = t('category_required') || 'Category is required';
    }
    
    if (!formData.productBrandId) {
      newErrors.productBrandId = t('brand_required') || 'Brand is required';
    }
    
    if (formData.price === '' || formData.price === null || parseFloat(formData.price) < 0) {
      newErrors.price = t('valid_price_required') || 'Valid price is required';
    }
    
    if (formData.costPrice === '' || formData.costPrice === null || parseFloat(formData.costPrice) < 0) {
      newErrors.costPrice = t('valid_cost_price_required') || 'Valid cost price is required';
    }
    
    if (formData.stockQuantity === '' || formData.stockQuantity === null || parseInt(formData.stockQuantity) < 0) {
      newErrors.stockQuantity = t('valid_stock_quantity_required') || 'Valid stock quantity is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      setServerError(t('please_fill_required_fields') || 'Please fill all required fields');
      return;
    }
    
    setLoading(true);
    setServerError(''); // Clear any previous server errors
    try {
      const productData = {
        Name: formData.name,
        Description: formData.description,
        SKU: formData.sku?.trim() || null,
        Barcode: formData.barcode?.trim() || null,
        ProductCategoryId: parseInt(formData.categoryId), // ✅ Changed from CategoryId to ProductCategoryId
        SalePrice: parseFloat(formData.price), // ✅ Changed from Price to SalePrice
        CostPrice: parseFloat(formData.costPrice),
        StockQuantity: parseInt(formData.stockQuantity),
        MinStockLevel: formData.minStockLevel ? parseInt(formData.minStockLevel) : 0,
        SupplierId: formData.supplierId ? parseInt(formData.supplierId) : null,
        ProductBrandId: formData.productBrandId ? parseInt(formData.productBrandId) : null,
        ModelNumber: formData.modelNumber?.trim() || null,
        IsActive: formData.isActive,
      };
      
      const response = await addProduct(productData);
      toast.success(t('product_created_successfully') || 'Product created successfully');
      
      if (onProductCreated) {
        onProductCreated(response?.payload || response);
      }
      
      handleClose();
    } catch (error) {
      console.error('Error creating product:', error);
      // Display all backend errors at the bottom of the dialog, no toasts
      const errorMessage = error.response?.data?.message || error.message || t('failed_to_create_product') || 'Failed to create product';
      setServerError(errorMessage);
      
      // Scroll to the error message
      setTimeout(() => {
        const errorElement = document.getElementById('server-error-message');
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      sku: '',
      barcode: '',
      categoryId: '',
      price: '',
      costPrice: '',
      stockQuantity: '',
      minStockLevel: '',
      supplierId: '',
      productBrandId: '',
      modelNumber: '',
      isActive: true,
    });
    setErrors({});
    setServerError('');
    onClose();
  };

  // Category dialog handlers
  const handleAddCategory = async () => {
    if (!newCategory.name.trim()) {
      toast.error(t('category_name_required') || 'Category name is required');
      return;
    }
    
    try {
      const response = await addCategory({ CategoryName: newCategory.name });
      
      if (response.status === 'Success' || response) {
        const createdCategory = response?.payload || response;
        toast.success(t('category_created_successfully') || 'Category created successfully');
        
        // Refresh categories list
        const categoriesResponse = await getCategories();
        const categoriesData = categoriesResponse?.payload || categoriesResponse || [];
        setCategories(Array.isArray(categoriesData) ? categoriesData : []);
        
        setFormData(prev => ({ ...prev, categoryId: createdCategory.Id }));
        setNewCategory({ name: '' });
        setOpenCategoryDialog(false);
        
        // ✅ Notify parent component to update Redux store
        if (onCategoryCreated) {
          onCategoryCreated(createdCategory);
        }
      }
    } catch (error) {
      console.error('Error creating category:', error);
      toast.error(t('failed_to_create_category') || 'Failed to create category');
    }
  };

  // Supplier dialog handlers
  const handleAddSupplier = async () => {
    if (!newSupplier.name.trim()) {
      toast.error(t('supplier_name_required') || 'Supplier name is required');
      return;
    }
    
    try {
      const supplierData = {
        Name: newSupplier.name.trim(),
        Contactperson: newSupplier.contactPerson?.trim() || null,
        Email: newSupplier.email?.trim() || null,
        MobileNumber: newSupplier.phone?.trim() || null,
        Street: newSupplier.address?.trim() || null,
        StreetNumber: null,
        PostCode: null,
        City: null,
        Country: null,
        BTWNumber: null,
        Notes: null
      };
      
      const response = await addSupplier(supplierData);
      toast.success(t('supplier_created_successfully') || 'Supplier created successfully');
      
      // Refresh suppliers list
      const suppliersResponse = await getSuppliers();
      const suppliersData = suppliersResponse?.payload || suppliersResponse || [];
      setSuppliers(Array.isArray(suppliersData) ? suppliersData : []);
      
      const addedSupplierId = response.Id || response.id || response.payload?.Id;
      if (addedSupplierId) {
        setFormData(prev => ({ ...prev, supplierId: addedSupplierId }));
      }
      
      setNewSupplier({ name: '', contactPerson: '', email: '', phone: '', address: '' });
      setOpenSupplierDialog(false);
      
      // ✅ Notify parent component to update Redux store
      if (onSupplierCreated) {
        onSupplierCreated(response?.payload || response);
      }
    } catch (error) {
      console.error('Error creating supplier:', error);
      
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.response?.data?.errors) {
        toast.error(t('please_fix_supplier_form_errors') || 'Please fix the supplier form errors');
      } else if (error.message) {
        toast.error(error.message);
      } else {
        toast.error(t('failed_to_create_supplier') || 'Failed to add supplier. Please try again.');
      }
    }
  };

  // Brand dialog handlers
  const handleAddBrand = async () => {
    if (!newBrand.BrandName.trim()) {
      toast.error(t('brand_name_required') || 'Brand name is required');
      return;
    }
    
    try {
      const brandData = {
        BrandName: newBrand.BrandName.trim(),
        Description: newBrand.Description?.trim() || null,
        LogoUrl: newBrand.LogoUrl?.trim() || null,
        Website: newBrand.Website?.trim() || null
      };
      
      const response = await addProductBrand(brandData);
      toast.success(t('brand_created_successfully') || 'Brand created successfully');
      
      // Refresh brands list
      const brandsResponse = await getProductBrands();
      const brandsData = brandsResponse?.payload || brandsResponse || [];
      setBrands(Array.isArray(brandsData) ? brandsData : []);
      
      const addedBrandId = response.Id || response.id || response.payload?.Id;
      if (addedBrandId) {
        setFormData(prev => ({ ...prev, productBrandId: addedBrandId }));
      }
      
      setNewBrand({ BrandName: '', Description: '', LogoUrl: '', Website: '' });
      setOpenBrandDialog(false);
      
      // ✅ Notify parent component to update Redux store
      if (onBrandCreated) {
        onBrandCreated(response?.payload || response);
      }
    } catch (error) {
      console.error('Error creating brand:', error);
      
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else if (error.response?.data?.errors) {
        toast.error(t('please_fix_brand_form_errors') || 'Please fix the brand form errors');
      } else if (error.message) {
        toast.error(error.message);
      } else {
        toast.error(t('failed_to_create_brand') || 'Failed to add brand. Please try again.');
      }
    }
  };

  const categoryOptions = [
    { id: 'ADD_NEW', label: t('add_new_category') || 'Add New Category', isAddNew: true },
    ...categories.map(c => ({ id: c.Id, label: c.Name || c.CategoryName || '' })),
  ];
  const supplierOptions = [
    { id: 'ADD_NEW', label: t('add_new_supplier') || 'Add New Supplier', isAddNew: true },
    ...suppliers.map(s => ({ id: s.Id, label: s.Name || s.SupplierName || 'Unknown Supplier' })),
  ];
  const brandOptions = [
    { id: 'ADD_NEW', label: t('add_new_brand') || 'Add New Brand', isAddNew: true },
    ...brands.map(b => ({ id: b.Id, label: b.BrandName || '' })),
  ];

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6" fontWeight="600">
              {t('add_new_product') || 'Add New Product'}
            </Typography>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          <Grid container spacing={2.5}>
            {/* Product Name */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label={t('product_name') || 'Product Name'}
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                error={!!errors.name}
                helperText={errors.name}
              />
            </Grid>
            
            {/* Model Number */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('model_number') || 'Model Number'}
                name="modelNumber"
                value={formData.modelNumber}
                onChange={handleInputChange}
              />
            </Grid>
            
            {/* SKU */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('sku') || 'SKU'}
                name="sku"
                value={formData.sku}
                onChange={handleInputChange}
                error={!!errors.sku}
                helperText={errors.sku}
              />
            </Grid>
            
            {/* Barcode */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('barcode') || 'Barcode'}
                name="barcode"
                value={formData.barcode}
                onChange={handleInputChange}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={t('generate_barcode') || 'Generate barcode'}>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setFormData(prev => ({ ...prev, barcode: generateBarcode() }))}
                          sx={{
                            minWidth: 'auto',
                            px: 1,
                            py: 0.25,
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            borderColor: 'primary.main',
                            color: 'primary.main',
                            '&:hover': {
                              bgcolor: 'primary.main',
                              color: 'white',
                            },
                          }}
                        >
                          {t('generate') || 'Generate'}
                        </Button>
                      </Tooltip>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            
            {/* Supplier */}
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={supplierOptions}
                value={supplierOptions.find(o => !o.isAddNew && o.id === formData.supplierId) || null}
                onChange={(_, newValue) => {
                  if (!newValue) { setFormData(prev => ({ ...prev, supplierId: '' })); return; }
                  if (newValue.isAddNew) { setOpenSupplierDialog(true); }
                  else {
                    setFormData(prev => ({ ...prev, supplierId: newValue.id }));
                    if (serverError) setServerError('');
                  }
                }}
                filterOptions={(options, { inputValue }) => {
                  const addNew = options.find(o => o.isAddNew);
                  const filtered = options.filter(o => !o.isAddNew && o.label.toLowerCase().includes(inputValue.toLowerCase()));
                  return addNew ? [addNew, ...filtered] : filtered;
                }}
                getOptionLabel={(option) => option?.isAddNew ? '' : (option?.label || '')}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                renderOption={(props, option) => (
                  <li {...props} key={String(option.id)}>
                    {option.isAddNew ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main', fontWeight: 600 }}>
                        <AddIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                        {option.label}
                      </Box>
                    ) : option.label}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('supplier') || 'Supplier'}
                    placeholder={t('search_supplier') || 'Search supplier...'}
                  />
                )}
                noOptionsText={t('no_suppliers_available') || 'No suppliers available'}
                blurOnSelect
                clearOnBlur={false}
              />
            </Grid>
            
            {/* Category */}
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={categoryOptions}
                value={categoryOptions.find(o => !o.isAddNew && o.id === formData.categoryId) || null}
                onChange={(_, newValue) => {
                  if (!newValue) {
                    setFormData(prev => ({ ...prev, categoryId: '' }));
                    setErrors(prev => ({ ...prev, categoryId: t('category_required') || 'Category is required' }));
                    return;
                  }
                  if (newValue.isAddNew) { setOpenCategoryDialog(true); }
                  else {
                    setFormData(prev => ({ ...prev, categoryId: newValue.id }));
                    setErrors(prev => ({ ...prev, categoryId: '' }));
                    if (serverError) setServerError('');
                  }
                }}
                filterOptions={(options, { inputValue }) => {
                  const addNew = options.find(o => o.isAddNew);
                  const filtered = options.filter(o => !o.isAddNew && o.label.toLowerCase().includes(inputValue.toLowerCase()));
                  return addNew ? [addNew, ...filtered] : filtered;
                }}
                getOptionLabel={(option) => option?.isAddNew ? '' : (option?.label || '')}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                renderOption={(props, option) => (
                  <li {...props} key={String(option.id)}>
                    {option.isAddNew ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main', fontWeight: 600 }}>
                        <AddIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                        {option.label}
                      </Box>
                    ) : option.label}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label={`${t('category') || 'Category'} *`}
                    error={!!errors.categoryId}
                    helperText={errors.categoryId}
                    placeholder={t('search_category') || 'Search category...'}
                  />
                )}
                noOptionsText={t('no_categories_available') || 'No categories available'}
                blurOnSelect
                clearOnBlur={false}
              />
            </Grid>
            
            {/* Brand */}
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={brandOptions}
                value={brandOptions.find(o => !o.isAddNew && o.id === formData.productBrandId) || null}
                onChange={(_, newValue) => {
                  if (!newValue) {
                    setFormData(prev => ({ ...prev, productBrandId: '' }));
                    setErrors(prev => ({ ...prev, productBrandId: t('brand_required') || 'Brand is required' }));
                    return;
                  }
                  if (newValue.isAddNew) { setOpenBrandDialog(true); }
                  else {
                    setFormData(prev => ({ ...prev, productBrandId: newValue.id }));
                    setErrors(prev => ({ ...prev, productBrandId: '' }));
                    if (serverError) setServerError('');
                  }
                }}
                filterOptions={(options, { inputValue }) => {
                  const addNew = options.find(o => o.isAddNew);
                  const filtered = options.filter(o => !o.isAddNew && o.label.toLowerCase().includes(inputValue.toLowerCase()));
                  return addNew ? [addNew, ...filtered] : filtered;
                }}
                getOptionLabel={(option) => option?.isAddNew ? '' : (option?.label || '')}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                renderOption={(props, option) => (
                  <li {...props} key={String(option.id)}>
                    {option.isAddNew ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main', fontWeight: 600 }}>
                        <AddIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                        {option.label}
                      </Box>
                    ) : option.label}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label={`${t('brand') || 'Brand'} *`}
                    error={!!errors.productBrandId}
                    helperText={errors.productBrandId}
                    placeholder={t('search_brand') || 'Search brand...'}
                  />
                )}
                noOptionsText={t('no_brands_available') || 'No brands available'}
                blurOnSelect
                clearOnBlur={false}
              />
            </Grid>
            
            {/* Sale Price */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label={t('sale_price') || 'Sale Price'}
                name="price"
                type="number"
                value={formData.price}
                onChange={handleInputChange}
                error={!!errors.price}
                helperText={errors.price}
                InputProps={{
                  startAdornment: <InputAdornment position="start">€</InputAdornment>,
                }}
              />
            </Grid>
            
            {/* Cost Price */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label={t('cost_price') || 'Cost Price'}
                name="costPrice"
                type="number"
                value={formData.costPrice}
                onChange={handleInputChange}
                error={!!errors.costPrice}
                helperText={errors.costPrice}
                InputProps={{
                  startAdornment: <InputAdornment position="start">€</InputAdornment>,
                }}
              />
            </Grid>
            
            {/* Stock Quantity */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label={t('stock_quantity') || 'Stock Quantity'}
                name="stockQuantity"
                type="number"
                value={formData.stockQuantity}
                onChange={handleInputChange}
                error={!!errors.stockQuantity}
                helperText={errors.stockQuantity}
              />
            </Grid>
            
            {/* Min Stock Level */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('min_stock_level') || 'Min Stock Level'}
                name="minStockLevel"
                type="number"
                value={formData.minStockLevel}
                onChange={handleInputChange}
              />
            </Grid>
            
            {/* Description */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t('description') || 'Description'}
                name="description"
                value={formData.description}
                onChange={handleInputChange}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={handleInputChange}
                    name="isActive"
                  />
                }
                label={t('active') || 'Active'}
              />
            </Grid>
            
            {/* Server Error Message */}
            {serverError && (
              <Grid item xs={12}>
                <Box
                  id="server-error-message"
                  sx={{
                    p: 2,
                    backgroundColor: '#ffebee',
                    borderRadius: 1,
                    border: '1px solid #ef5350',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#d32f2f',
                      fontWeight: 500,
                      flex: 1
                    }}
                  >
                    {serverError}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => setServerError('')}
                    sx={{ color: '#d32f2f' }}
                  >
                    ×
                  </IconButton>
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} disabled={loading}>
            {t('cancel') || 'Cancel'}
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? t('creating') || 'Creating...' : t('create_product') || 'Create Product'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category Dialog */}
      <Dialog open={openCategoryDialog} onClose={() => setOpenCategoryDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('add_new_category') || 'Add New Category'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label={t('category_name') || 'Category Name'}
            value={newCategory.name}
            onChange={(e) => setNewCategory({ name: e.target.value })}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCategoryDialog(false)}>
            {t('cancel') || 'Cancel'}
          </Button>
          <Button onClick={handleAddCategory} variant="contained">
            {t('add') || 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Supplier Dialog */}
      <Dialog open={openSupplierDialog} onClose={() => setOpenSupplierDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('add_new_supplier') || 'Add New Supplier'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label={t('supplier_name') || 'Supplier Name'}
                value={newSupplier.name}
                onChange={(e) => setNewSupplier({ ...newSupplier, name: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('contact_person') || 'Contact Person'}
                value={newSupplier.contactPerson}
                onChange={(e) => setNewSupplier({ ...newSupplier, contactPerson: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('email') || 'Email'}
                type="email"
                value={newSupplier.email}
                onChange={(e) => setNewSupplier({ ...newSupplier, email: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('phone') || 'Phone'}
                value={newSupplier.phone}
                onChange={(e) => setNewSupplier({ ...newSupplier, phone: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label={t('address') || 'Address'}
                value={newSupplier.address}
                onChange={(e) => setNewSupplier({ ...newSupplier, address: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSupplierDialog(false)}>
            {t('cancel') || 'Cancel'}
          </Button>
          <Button onClick={handleAddSupplier} variant="contained">
            {t('add') || 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Brand Dialog */}
      <Dialog open={openBrandDialog} onClose={() => setOpenBrandDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('add_new_brand') || 'Add New Brand'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label={t('brand_name') || 'Brand Name'}
                value={newBrand.BrandName}
                onChange={(e) => setNewBrand({ ...newBrand, BrandName: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('website') || 'Website'}
                value={newBrand.Website}
                onChange={(e) => setNewBrand({ ...newBrand, Website: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t('description') || 'Description'}
                value={newBrand.Description}
                onChange={(e) => setNewBrand({ ...newBrand, Description: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenBrandDialog(false)}>
            {t('cancel') || 'Cancel'}
          </Button>
          <Button onClick={handleAddBrand} variant="contained">
            {t('add') || 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddProductDialog;
